import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Building2, Send, Loader2 } from "lucide-react";
import { COMPANY, PHONE_DISPLAY, PHONE_HREF, EMAIL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Call us", value: PHONE_DISPLAY, href: PHONE_HREF, testid: "contact-phone" },
  { icon: Mail, label: "Email us", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
  { icon: Building2, label: "Company", value: COMPANY, href: null, testid: "contact-company" },
];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-royal-deep/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-gold/60";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent successfully. We will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-royal py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold sm:text-xs">Get in touch</p>
          <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Let's explore your next market.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            Tell us where you want to go. We will respond with a clear view of the opportunity and the practical
            steps to reach it.
          </p>

          <div className="mt-12 space-y-4">
            {INFO.map(({ icon: Icon, label, value, href, testid }) => {
              const inner = (
                <>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <Icon size={18} />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
                    <p className="mt-1 text-sm font-medium text-white">{value}</p>
                  </div>
                </>
              );
              const cls =
                "flex items-center gap-5 rounded-xl border border-white/10 bg-white/[0.02] p-5 transition-colors duration-300 hover:border-gold/40";
              return href ? (
                <a key={label} href={href} className={cls} data-testid={testid}>
                  {inner}
                </a>
              ) : (
                <div key={label} className={cls} data-testid={testid}>
                  {inner}
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-gold/20 bg-royal-card/70 p-8 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-10"
          data-testid="enquiry-form"
        >
          <h3 className="font-serif text-2xl font-semibold text-white">Send an enquiry</h3>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-name" className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Name *
              </label>
              <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-email" className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Email *
              </label>
              <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-phone" className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Phone
              </label>
              <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-company" className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Company
              </label>
              <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Message *
              </label>
              <textarea id="enquiry-message" data-testid="enquiry-message-input" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your business and the markets you're exploring..." className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-btn"
            className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-royal-deep transition-colors duration-300 hover:bg-gold-pale disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
